import styles from "src/page/home/home.module.scss";
import { Button, MenuItem, Select, SelectChangeEvent, Skeleton } from "@mui/material";
import { dateTimeFormat, getDecimalNumber } from "src/utils/utils";
import { CardSetting, getUserCardAction, singlePayAction } from "src/page/home/homeSlice";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/page/store";
import moment from "moment";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteCardSettingAction } from "src/page/cardSetting/cardSettingSlice";
import swal from "sweetalert2";
import { EMPTY_STRING } from "../constant/constant";

interface HomeWalletCardProp {
  value: number;
  activeCard: number;
  card: CardSetting;
  cardContainerOver?: string;
}

export default function HomeWalletCard({ value, activeCard, card, cardContainerOver }: HomeWalletCardProp) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedWalletId, setSelectedWalletId] = useState(EMPTY_STRING);
  const { wallets, walletDetails } = useAppSelector((state) => state.home);
  const { userInfo, defaultCardSettingExpiry } = useAppSelector((state) => state.auth);

  useEffect(() => {
    setSelectedWalletId(card?.wallet_id);
  }, [card?.wallet_id]);

  const handleOrgSelect = (event: SelectChangeEvent) => {
    setSelectedWalletId(event.target.value);
    const walletId = event.target.value;

    const data = {
      expireAt: moment()
        .add(defaultCardSettingExpiry ?? 12, "hours")
        .toISOString(),
      cardId: card?.card_id,
      walletId
    };

    dispatch(singlePayAction(data)).then(() => {
      dispatch(getUserCardAction());
    });
  };

  const walletDetail = walletDetails[card?.wallet_id];
  const walletList = wallets[card?.wallet_id]?.list || [];

  const deleteCard = () => {
    swal
      .fire({
        title: "Are you sure! you want to remove this card setting?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#1e293b",
        confirmButtonText: "Yes, remove it!",
        customClass: {
          popup: "swal2-popup-custom"
        }
      })
      .then((result) => {
        if (result.isConfirmed) {
          dispatch(
            deleteCardSettingAction(card.card_id, card.wallet_id, () => {
              dispatch(getUserCardAction());
            })
          );
        }
      });
  };

  return (
    <div className={`${styles.cardContainer} ${cardContainerOver}`}>
      {value === activeCard ? (
        <div className={styles.card}>
          <div className={styles.indicator}>
            <div className={`${styles.greenCircle} ${card ? styles.greenactive : EMPTY_STRING}`}></div>
            <div className={` ${styles.redCircle} ${!card ? styles.redactive : EMPTY_STRING}`}></div>
          </div>
          {card ? (
            <>
              {userInfo?.id === card?.user_id && (
                <Button variant="contained" className={styles.moreIcon} onClick={deleteCard}>
                  <DeleteOutlineIcon fontSize="inherit" className={styles.icon} />
                </Button>
              )}
              <div className={styles.cardContent}>
                <div className={styles.walletName}>Paying for {walletDetail?.wallet?.name}</div>
                <div className={styles.balance}>
                  ${walletDetail?.wallet?.balance ? getDecimalNumber(walletDetail?.wallet?.balance) : "0.00"}
                  Available
                </div>
                <div className={styles.balance}>
                  {walletDetail?.wallet?.participant
                    ? walletDetail?.wallet?.participant?.firstName + " " + walletDetail?.wallet?.participant?.lastname
                    : walletDetail?.wallet?.group?.name}
                </div>
                <div className={styles.cardno}>Card ends in {card?.ppan?.slice(-4)}</div>
              </div>
              {card?.card_setting_status === "ONCE" ? (
                <div className={styles.caution}>Money available on the card until used</div>
              ) : (
                card?.expires_at && (
                  <div className={styles.caution}>
                    <span>
                      {`*Money Available On Card Until: `}
                      {dateTimeFormat(Number(card?.expires_at)).datetime}
                    </span>
                  </div>
                )
              )}
            </>
          ) : (
            <div className={styles.cardContent}>
              <div className={styles.balance}>No wallet selected</div>
              <div className={styles.cardno}>Please add a wallet to pay</div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.indicator}>
            <div className={`${styles.greenCircle}`}></div>
            <div className={` ${styles.redCircle}`}></div>
          </div>
          <div className={styles.cardContent}>
            <Skeleton variant="text" sx={{ fontSize: "2rem", width: "-webkit-fill-available" }} />
            <Skeleton variant="text" sx={{ fontSize: "2rem", width: "-webkit-fill-available" }} />
            <Skeleton variant="text" sx={{ fontSize: "2rem", width: "-webkit-fill-available" }} />
          </div>
          <div className={styles.caution}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "30vw" }} />
          </div>
        </div>
      )}

      <div className={styles.wallet}>
        {card ? (
          <>
            <span className={styles.label}>Select a different wallet</span>
            <Select
              className={styles.selectOption}
              value={selectedWalletId}
              onChange={handleOrgSelect}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              sx={{
                height: 50,
                minWidth: 150
              }}
            >
              {value === activeCard &&
                walletList?.map((option) => (
                  <MenuItem key={option.id} value={option.id.toString()}>
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
          </>
        ) : (
          <Button
            variant="contained"
            className={styles.btn}
            onClick={() => {
              navigate("/newPayment");
            }}
          >
            Select Wallet To Pay
          </Button>
        )}
      </div>
    </div>
  );
}
